// src/components/Dashboard/Dashboard.jsx
import { $t } from 'hooks/i18n';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import axios from 'axios';

import './Dashboard.css';
import useApp from 'hooks/useApp';
import useAuth from 'hooks/useAuth';
import useUtils from 'hooks/useUtils';

// ============================================================
// API BASE
// ============================================================
const API_BASE = process.env.REACT_APP_API_PATH || 'https://localhost:5001/api/';

// ============================================================
// DASHBOARD
// ============================================================
const Dashboard = () => {
    // --------------------------------------------------------
    // HOOKS - Called inside component
    // --------------------------------------------------------
    const auth = useAuth();
    const app = useApp();
    const utils = useUtils();

    // ============================================================
    // TOKEN HELPER - Uses auth from hooks
    // ============================================================
    const getAccessToken = useCallback(() => {
        // 1. Try auth context first
        if (auth?.accessToken) {
            return auth.accessToken;
        }

        // 2. localStorage
        const localToken = localStorage.getItem('accessToken');
        if (localToken) {
            return localToken;
        }

        // 3. sessionStorage
        const sessionToken = sessionStorage.getItem('accessToken');
        if (sessionToken) {
            return sessionToken;
        }

        // 4. Old token keys
        const oldLocalToken = localStorage.getItem('token');
        if (oldLocalToken) {
            return oldLocalToken;
        }

        const oldSessionToken = sessionStorage.getItem('token');
        if (oldSessionToken) {
            return oldSessionToken;
        }

        // 5. Cookie
        const cookieToken = document.cookie
            .split('; ')
            .find(row => row.startsWith('accessToken='))
            ?.split('=')[1];
        if (cookieToken) {
            return cookieToken;
        }

        return null;
    }, [auth]);

    // ============================================================
    // AXIOS CONFIG
    // ============================================================
    const getAuthConfig = useCallback(() => {
        const token = getAccessToken();
        return {
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                'Content-Type': 'application/json'
            }
        };
    }, [getAccessToken]);

    // ============================================================
    // DATE HELPER
    // ============================================================
    const formatApiDate = useCallback((date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // ============================================================
    // API SERVICE
    // ============================================================
    const dashboardApi = useMemo(() => ({
        getSummary: () => axios.get(`${API_BASE}bdashboard/summary`, getAuthConfig()),
        getBranchInfo: () => axios.get(`${API_BASE}bdashboard/branch-info`, getAuthConfig()),
        getLoansByStatus: () => axios.get(`${API_BASE}bdashboard/loans-by-status`, getAuthConfig()),
        getOverdueLoans: () => axios.get(`${API_BASE}bdashboard/overdue-loans`, getAuthConfig()),
        getRecentTransactions: (count = 10) => 
            axios.get(`${API_BASE}bdashboard/recent-transactions?count=${count}`, getAuthConfig()),
        getPortfolioAtRisk: () => axios.get(`${API_BASE}bdashboard/portfolio-at-risk`, getAuthConfig()),
        getCollectionReport: (fromDate, toDate) => {
            const params = new URLSearchParams();
            if (fromDate) params.append('fromDate', formatApiDate(fromDate));
            if (toDate) params.append('toDate', formatApiDate(toDate));
            return axios.get(`${API_BASE}bdashboard/collection-report?${params.toString()}`, getAuthConfig());
        },
        getDisbursementReport: (fromDate, toDate) => {
            const params = new URLSearchParams();
            if (fromDate) params.append('fromDate', formatApiDate(fromDate));
            if (toDate) params.append('toDate', formatApiDate(toDate));
            return axios.get(`${API_BASE}bdashboard/disbursement-report?${params.toString()}`, getAuthConfig());
        },
        getMemberReport: (fromDate, toDate) => {
            const params = new URLSearchParams();
            if (fromDate) params.append('fromDate', formatApiDate(fromDate));
            if (toDate) params.append('toDate', formatApiDate(toDate));
            return axios.get(`${API_BASE}bdashboard/member-report?${params.toString()}`, getAuthConfig());
        }
    }), [getAuthConfig, formatApiDate]);

    // --------------------------------------------------------
    // TOKEN
    // --------------------------------------------------------
    const [accessToken, setAccessToken] = useState(() => getAccessToken());

    // --------------------------------------------------------
    // AUTHENTICATION
    // --------------------------------------------------------
    useEffect(() => {
        const checkToken = () => {
            const token = getAccessToken();
            console.log('Dashboard accessToken:', token ? `${token.substring(0, 20)}...` : 'NOT FOUND');
            setAccessToken(token);
        };

        checkToken();
        window.addEventListener('storage', checkToken);
        const timer = setTimeout(checkToken, 300);

        return () => {
            window.removeEventListener('storage', checkToken);
            clearTimeout(timer);
        };
    }, [getAccessToken]);

    const isAuthenticated = !!accessToken;

    // --------------------------------------------------------
    // LOADING STATES
    // --------------------------------------------------------
    const [loadingStates, setLoadingStates] = useState({
        summary: true,
        branchInfo: true,
        loansByStatus: true,
        overdueLoans: true,
        recentTransactions: true,
        portfolioAtRisk: true,
        collectionReport: true,
        disbursementReport: true,
        memberReport: true
    });

    // --------------------------------------------------------
    // ERRORS
    // --------------------------------------------------------
    const [errors, setErrors] = useState({});

    // --------------------------------------------------------
    // LOAN DETAIL
    // --------------------------------------------------------
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [showLoanDetail, setShowLoanDetail] = useState(false);

    // --------------------------------------------------------
    // TRANSACTION COUNT
    // --------------------------------------------------------
    const [transactionCount, setTransactionCount] = useState(10);

    // --------------------------------------------------------
    // REPORT FILTERS
    // --------------------------------------------------------
    const [reportFilters, setReportFilters] = useState({
        collectionFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        collectionTo: new Date(),
        disbursementFrom: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        disbursementTo: new Date(),
        memberFrom: new Date(new Date().setMonth(new Date().getMonth() - 3)),
        memberTo: new Date()
    });

    // --------------------------------------------------------
    // DATA
    // --------------------------------------------------------
    const [summary, setSummary] = useState({
        totalMembers: 0,
        totalActiveLoans: 0,
        totalDisbursed: 0,
        totalCollection: 0,
        overdueLoans: 0,
        branchCode: '',
        userName: '',
        lastUpdated: new Date()
    });

    const [branchInfo, setBranchInfo] = useState({
        branchCode: '',
        branchName: '',
        address: '',
        phone: '',
        email: '',
        district: '',
        state: '',
        userName: '',
        userEmail: '',
        userRole: ''
    });

    const [loansByStatus, setLoansByStatus] = useState({
        activeLoans: 0,
        closedLoans: 0,
        rejectedLoans: 0,
        totalLoans: 0
    });

    const [overdueLoans, setOverdueLoans] = useState([]);
    const [recentTransactions, setRecentTransactions] = useState([]);

    const [portfolioAtRisk, setPortfolioAtRisk] = useState({
        branchCode: '',
        totalOutstanding: 0,
        totalOverdueAmount: 0,
        par30: 0,
        par60: 0,
        par90: 0,
        par30Percentage: 0,
        par60Percentage: 0,
        par90Percentage: 0,
        overdueLoans: 0,
        activeLoans: 0,
        reportDate: new Date()
    });

    const [collectionReport, setCollectionReport] = useState({
        branchCode: '',
        fromDate: new Date(),
        toDate: new Date(),
        monthlyData: [],
        totalCollection: 0,
        totalTransactions: 0,
        dailyCollection: [],
        averageDailyCollection: 0
    });

    const [disbursementReport, setDisbursementReport] = useState({
        branchCode: '',
        fromDate: new Date(),
        toDate: new Date(),
        monthlyData: [],
        totalDisbursed: 0,
        totalLoans: 0,
        averageLoanSize: 0
    });

    const [memberReport, setMemberReport] = useState({
        branchCode: '',
        fromDate: new Date(),
        toDate: new Date(),
        members: [],
        totalMembers: 0,
        maleCount: 0,
        femaleCount: 0,
        averageAge: 0,
        occupationalDistribution: {}
    });

    // ========================================================
    // COMMON ERROR HANDLER
    // ========================================================
    const handleApiError = useCallback((error, section) => {
        console.error(`${section} error:`, error);
        if (error.response?.status === 401) {
            console.error('401 Unauthorized - accessToken may be expired');
        }
        setErrors(prev => ({
            ...prev,
            [section]: error.response?.data?.message || error.response?.data || error.message || 'Unknown error'
        }));
    }, []);

    // ========================================================
    // SUMMARY
    // ========================================================
    const fetchSummary = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, summary: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, summary: true }));
        try {
            const response = await dashboardApi.getSummary();
            console.log('Summary response:', response.data);
            setSummary(response.data || {});
            setErrors(prev => ({ ...prev, summary: null }));
        } catch (error) {
            handleApiError(error, 'summary');
        } finally {
            setLoadingStates(prev => ({ ...prev, summary: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError]);

    // ========================================================
    // BRANCH INFO
    // ========================================================
    const fetchBranchInfo = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, branchInfo: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, branchInfo: true }));
        try {
            const response = await dashboardApi.getBranchInfo();
            setBranchInfo(response.data || {});
            setErrors(prev => ({ ...prev, branchInfo: null }));
        } catch (error) {
            handleApiError(error, 'branchInfo');
        } finally {
            setLoadingStates(prev => ({ ...prev, branchInfo: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError]);

    // ========================================================
    // LOANS BY STATUS
    // ========================================================
    const fetchLoansByStatus = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, loansByStatus: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, loansByStatus: true }));
        try {
            const response = await dashboardApi.getLoansByStatus();
            setLoansByStatus(response.data || {});
            setErrors(prev => ({ ...prev, loansByStatus: null }));
        } catch (error) {
            handleApiError(error, 'loansByStatus');
        } finally {
            setLoadingStates(prev => ({ ...prev, loansByStatus: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError]);

    // ========================================================
    // OVERDUE LOANS
    // ========================================================
    const fetchOverdueLoans = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, overdueLoans: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, overdueLoans: true }));
        try {
            const response = await dashboardApi.getOverdueLoans();
            setOverdueLoans(Array.isArray(response.data) ? response.data : []);
            setErrors(prev => ({ ...prev, overdueLoans: null }));
        } catch (error) {
            handleApiError(error, 'overdueLoans');
        } finally {
            setLoadingStates(prev => ({ ...prev, overdueLoans: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError]);

    // ========================================================
    // RECENT TRANSACTIONS
    // ========================================================
    const fetchRecentTransactions = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, recentTransactions: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, recentTransactions: true }));
        try {
            const response = await dashboardApi.getRecentTransactions(transactionCount);
            setRecentTransactions(Array.isArray(response.data) ? response.data : []);
            setErrors(prev => ({ ...prev, recentTransactions: null }));
        } catch (error) {
            handleApiError(error, 'recentTransactions');
        } finally {
            setLoadingStates(prev => ({ ...prev, recentTransactions: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError, transactionCount]);

    // ========================================================
    // PORTFOLIO AT RISK
    // ========================================================
    const fetchPortfolioAtRisk = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, portfolioAtRisk: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, portfolioAtRisk: true }));
        try {
            const response = await dashboardApi.getPortfolioAtRisk();
            setPortfolioAtRisk(response.data || {});
            setErrors(prev => ({ ...prev, portfolioAtRisk: null }));
        } catch (error) {
            handleApiError(error, 'portfolioAtRisk');
        } finally {
            setLoadingStates(prev => ({ ...prev, portfolioAtRisk: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError]);

    // ========================================================
    // COLLECTION REPORT
    // ========================================================
    const fetchCollectionReport = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, collectionReport: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, collectionReport: true }));
        try {
            const response = await dashboardApi.getCollectionReport(
                reportFilters.collectionFrom,
                reportFilters.collectionTo
            );
            setCollectionReport(response.data || {});
            setErrors(prev => ({ ...prev, collectionReport: null }));
        } catch (error) {
            handleApiError(error, 'collectionReport');
        } finally {
            setLoadingStates(prev => ({ ...prev, collectionReport: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError, reportFilters.collectionFrom, reportFilters.collectionTo]);

    // ========================================================
    // DISBURSEMENT REPORT
    // ========================================================
    const fetchDisbursementReport = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, disbursementReport: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, disbursementReport: true }));
        try {
            const response = await dashboardApi.getDisbursementReport(
                reportFilters.disbursementFrom,
                reportFilters.disbursementTo
            );
            setDisbursementReport(response.data || {});
            setErrors(prev => ({ ...prev, disbursementReport: null }));
        } catch (error) {
            handleApiError(error, 'disbursementReport');
        } finally {
            setLoadingStates(prev => ({ ...prev, disbursementReport: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError, reportFilters.disbursementFrom, reportFilters.disbursementTo]);

    // ========================================================
    // MEMBER REPORT
    // ========================================================
    const fetchMemberReport = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            setLoadingStates(prev => ({ ...prev, memberReport: false }));
            return;
        }
        setLoadingStates(prev => ({ ...prev, memberReport: true }));
        try {
            const response = await dashboardApi.getMemberReport(
                reportFilters.memberFrom,
                reportFilters.memberTo
            );
            setMemberReport(response.data || {});
            setErrors(prev => ({ ...prev, memberReport: null }));
        } catch (error) {
            handleApiError(error, 'memberReport');
        } finally {
            setLoadingStates(prev => ({ ...prev, memberReport: false }));
        }
    }, [dashboardApi, getAccessToken, handleApiError, reportFilters.memberFrom, reportFilters.memberTo]);

    // ========================================================
    // LOAD ALL DATA
    // ========================================================
    const loadAllData = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            console.error('No accessToken found');
            return;
        }
        console.log('Loading dashboard with accessToken:', `${token.substring(0, 20)}...`);
        await Promise.allSettled([
            fetchSummary(),
            fetchBranchInfo(),
            fetchLoansByStatus(),
            fetchOverdueLoans(),
            fetchRecentTransactions(),
            fetchPortfolioAtRisk(),
            fetchCollectionReport(),
            fetchDisbursementReport(),
            fetchMemberReport()
        ]);
    }, [
        fetchSummary,
        fetchBranchInfo,
        fetchLoansByStatus,
        fetchOverdueLoans,
        fetchRecentTransactions,
        fetchPortfolioAtRisk,
        fetchCollectionReport,
        fetchDisbursementReport,
        fetchMemberReport,
        getAccessToken
    ]);

    // ========================================================
    // LOAD WHEN TOKEN EXISTS
    // ========================================================
    useEffect(() => {
        if (!accessToken) return;
        console.log('Access token available. Loading dashboard...');
        loadAllData();
    }, [accessToken, loadAllData]);

    // ========================================================
    // TRANSACTION COUNT CHANGE
    // ========================================================
    useEffect(() => {
        if (!accessToken) return;
        fetchRecentTransactions();
    }, [transactionCount, accessToken, fetchRecentTransactions]);

    // ========================================================
    // FORMAT CURRENCY
    // ========================================================
    const formatCurrency = useCallback((value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Number(value) || 0);
    }, []);

    // ========================================================
    // FORMAT DATE
    // ========================================================
    const formatDate = useCallback((date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }, []);

    // ========================================================
    // FORMAT DATE TIME
    // ========================================================
    const formatDateTime = useCallback((date) => {
        if (!date) return 'N/A';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'N/A';
        return d.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    // ========================================================
    // METRIC CARD
    // ========================================================
    const renderMetricCard = useCallback((title, value, icon, color, subtitle = null) => (
        <Card className="metric-card">
            <div className="flex align-items-center justify-content-between">
                <div>
                    <h6 className="text-500 text-sm m-0">{title}</h6>
                    <h2 className="m-0 font-bold text-2xl">{value}</h2>
                    {subtitle && <small className="text-500 text-sm">{subtitle}</small>}
                </div>
                <div className="metric-icon" style={{ backgroundColor: color }}>
                    <i className={icon} style={{ fontSize: '1.5rem', color: 'white' }} />
                </div>
            </div>
        </Card>
    ), []);

    // ========================================================
    // SKELETON
    // ========================================================
    const renderSkeletonCard = useCallback(() => (
        <Card>
            <Skeleton width="100%" height="80px" />
        </Card>
    ), []);

    // ========================================================
    // SUMMARY
    // ========================================================
    const renderSummaryCards = useCallback(() => {
        if (loadingStates.summary) {
            return (
                <div className="grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="col-12 md:col-6 lg:col-3">
                            {renderSkeletonCard()}
                        </div>
                    ))}
                </div>
            );
        }
        if (errors.summary) {
            return (
                <Message severity="error" text={`Summary: ${errors.summary}`} className="w-full" />
            );
        }
        return (
            <div className="grid">
                <div className="col-12 md:col-6 lg:col-3">
                    {renderMetricCard('Total Members', summary.totalMembers || 0, 'pi pi-users', '#42A5F5')}
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                    {renderMetricCard('Active Loans', summary.totalActiveLoans || 0, 'pi pi-credit-card', '#66BB6A')}
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                    {renderMetricCard('Total Disbursed', formatCurrency(summary.totalDisbursed), 'pi pi-arrow-up-right', '#FFA726')}
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                    {renderMetricCard('Overdue Loans', summary.overdueLoans || 0, 'pi pi-exclamation-triangle', '#EF5350',
                        `Collection: ${formatCurrency(summary.totalCollection)}`
                    )}
                </div>
            </div>
        );
    }, [loadingStates.summary, errors.summary, summary, renderMetricCard, renderSkeletonCard, formatCurrency]);

    // ========================================================
    // BRANCH TOOLBAR
    // ========================================================
    const renderBranchToolbar = useCallback(() => {
        if (loadingStates.branchInfo) {
            return <Skeleton width="200px" height="40px" />;
        }
        return (
            <div className="flex align-items-center gap-3">
                <Avatar
                    icon="pi pi-building"
                    shape="circle"
                    size="large"
                    style={{ backgroundColor: '#42A5F5', color: 'white' }}
                />
                <div>
                    <div className="text-xl font-bold">
                        {branchInfo.branchName || summary.branchCode || 'Branch'}
                    </div>
                    <div className="flex align-items-center gap-2 text-500">
                        <i className="pi pi-user" />
                        <span>{branchInfo.userName || summary.userName || 'User'}</span>
                        <Badge value={branchInfo.userRole || 'Employee'} severity="info" />
                    </div>
                </div>
            </div>
        );
    }, [loadingStates.branchInfo, branchInfo, summary]);

    // ========================================================
    // LOAN STATUS
    // ========================================================
    const renderLoanStatusSummary = useCallback(() => {
        if (loadingStates.loansByStatus) {
            return (
                <div className="flex justify-content-around p-3">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="text-center">
                            <Skeleton width="40px" height="30px" />
                            <Skeleton width="80px" height="15px" className="mt-1" />
                        </div>
                    ))}
                </div>
            );
        }
        if (errors.loansByStatus) {
            return (
                <Message severity="error" text={`Loans: ${errors.loansByStatus}`} className="w-full" />
            );
        }
        return (
            <div className="flex flex-wrap justify-content-around">
                <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{loansByStatus.activeLoans || 0}</div>
                    <div className="text-500 text-sm">Active Loans</div>
                </div>
                <Divider layout="vertical" />
                <div className="text-center">
                    <div className="text-2xl font-bold text-success">{loansByStatus.closedLoans || 0}</div>
                    <div className="text-500 text-sm">Closed Loans</div>
                </div>
                <Divider layout="vertical" />
                <div className="text-center">
                    <div className="text-2xl font-bold text-danger">{loansByStatus.rejectedLoans || 0}</div>
                    <div className="text-500 text-sm">Rejected Loans</div>
                </div>
                <Divider layout="vertical" />
                <div className="text-center">
                    <div className="text-2xl font-bold">{loansByStatus.totalLoans || 0}</div>
                    <div className="text-500 text-sm">Total Loans</div>
                </div>
            </div>
        );
    }, [loadingStates.loansByStatus, errors.loansByStatus, loansByStatus]);

    // ========================================================
    // PORTFOLIO
    // ========================================================
    const renderPortfolioAtRisk = useCallback(() => {
        if (loadingStates.portfolioAtRisk) {
            return <Skeleton width="100%" height="100px" />;
        }
        if (errors.portfolioAtRisk) {
            return <Message severity="error" text={`PAR: ${errors.portfolioAtRisk}`} className="w-full" />;
        }
        const data = portfolioAtRisk;
        return (
            <div className="grid">
                <div className="col-12 md:col-3">
                    <div className="text-center p-3 surface-ground border-round">
                        <div className="text-500">Total Outstanding</div>
                        <div className="text-xl font-bold">{formatCurrency(data.totalOutstanding)}</div>
                    </div>
                </div>
                <div className="col-12 md:col-3">
                    <div className="text-center p-3 surface-ground border-round">
                        <div className="text-500">PAR 30</div>
                        <div className="text-xl font-bold">{(Number(data.par30Percentage) || 0).toFixed(1)}%</div>
                        <small className="text-500">{formatCurrency(data.par30)}</small>
                    </div>
                </div>
                <div className="col-12 md:col-3">
                    <div className="text-center p-3 surface-ground border-round">
                        <div className="text-500">PAR 60</div>
                        <div className="text-xl font-bold">{(Number(data.par60Percentage) || 0).toFixed(1)}%</div>
                        <small className="text-500">{formatCurrency(data.par60)}</small>
                    </div>
                </div>
                <div className="col-12 md:col-3">
                    <div className="text-center p-3 surface-ground border-round">
                        <div className="text-500">PAR 90</div>
                        <div className="text-xl font-bold">{(Number(data.par90Percentage) || 0).toFixed(1)}%</div>
                        <small className="text-500">{formatCurrency(data.par90)}</small>
                    </div>
                </div>
            </div>
        );
    }, [loadingStates.portfolioAtRisk, errors.portfolioAtRisk, portfolioAtRisk, formatCurrency]);

    // ========================================================
    // COLLECTION CHART
    // ========================================================
    const renderCollectionChart = useCallback(() => {
        if (loadingStates.collectionReport) {
            return <Skeleton width="100%" height="250px" />;
        }
        if (errors.collectionReport) {
            return <Message severity="error" text={`Collection: ${errors.collectionReport}`} className="w-full" />;
        }
        const monthlyData = collectionReport.monthlyData || [];
        if (!monthlyData.length) {
            return <div className="text-center text-500 p-3">No collection data available</div>;
        }
        const chartData = {
            labels: monthlyData.map(item => item.period || ''),
            datasets: [{
                label: 'Collection',
                data: monthlyData.map(item => Number(item.totalCollection) || 0),
                backgroundColor: 'rgba(66,165,245,.6)',
                borderColor: '#42A5F5',
                borderWidth: 2,
                borderRadius: 4
            }]
        };
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true, ticks: { callback: value => formatCurrency(value) } } }
        };
        return <Chart type="bar" data={chartData} options={options} />;
    }, [loadingStates.collectionReport, errors.collectionReport, collectionReport, formatCurrency]);

    // ========================================================
    // DISBURSEMENT CHART
    // ========================================================
    const renderDisbursementChart = useCallback(() => {
        if (loadingStates.disbursementReport) {
            return <Skeleton width="100%" height="250px" />;
        }
        if (errors.disbursementReport) {
            return <Message severity="error" text={`Disbursement: ${errors.disbursementReport}`} className="w-full" />;
        }
        const monthlyData = disbursementReport.monthlyData || [];
        if (!monthlyData.length) {
            return <div className="text-center text-500 p-3">No disbursement data available</div>;
        }
        const chartData = {
            labels: monthlyData.map(item => item.period || ''),
            datasets: [{
                label: 'Disbursed',
                data: monthlyData.map(item => Number(item.totalDisbursed) || 0),
                backgroundColor: 'rgba(102,187,106,.6)',
                borderColor: '#66BB6A',
                borderWidth: 2,
                borderRadius: 4
            }]
        };
        const options = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true, ticks: { callback: value => formatCurrency(value) } } }
        };
        return <Chart type="bar" data={chartData} options={options} />;
    }, [loadingStates.disbursementReport, errors.disbursementReport, disbursementReport, formatCurrency]);

    // ========================================================
    // OVERDUE TABLE
    // ========================================================
    const renderOverdueTable = useCallback(() => {
        if (loadingStates.overdueLoans) {
            return <Skeleton width="100%" height="200px" />;
        }
        if (errors.overdueLoans) {
            return <Message severity="error" text={`Overdue: ${errors.overdueLoans}`} className="w-full" />;
        }
        if (!overdueLoans.length) {
            return (
                <div className="text-center p-4 text-500">
                    <i className="pi pi-check-circle" style={{ fontSize: '2rem', color: '#66BB6A' }} />
                    <p>No overdue loans found</p>
                </div>
            );
        }
        return (
            <DataTable
                value={overdueLoans.slice(0, 5)}
                responsiveLayout="scroll"
                size="small"
                stripedRows
                onRowClick={e => {
                    setSelectedLoan(e.data);
                    setShowLoanDetail(true);
                }}
            >
                <Column field="loanAccountNo" header="Account" />
                <Column field="memberName" header="Member" />
                <Column field="totalOverdueAmount" header="Overdue" body={row => formatCurrency(row.totalOverdueAmount)} />
                <Column field="overduePeriods" header="Periods" />
                <Column field="daysOverdue" header="Days" body={row => {
                    const days = Number(row.daysOverdue) || 0;
                    return <Badge value={`${days} days`} severity={days > 90 ? 'danger' : days > 60 ? 'warning' : 'info'} />;
                }} />
            </DataTable>
        );
    }, [loadingStates.overdueLoans, errors.overdueLoans, overdueLoans, formatCurrency]);

    // ========================================================
    // MEMBER DEMOGRAPHICS
    // ========================================================
    const renderMemberDemographics = useCallback(() => {
        if (loadingStates.memberReport) {
            return <Skeleton width="100%" height="200px" />;
        }
        if (errors.memberReport) {
            return <Message severity="error" text={`Members: ${errors.memberReport}`} className="w-full" />;
        }
        const data = memberReport;
        const distribution = data.occupationalDistribution || {};
        if (!Object.keys(distribution).length) {
            return <div className="text-center text-500 p-3">No demographic data available</div>;
        }
        const occupations = Object.entries(distribution)
            .sort((a, b) => Number(b[1]) - Number(a[1]))
            .slice(0, 5);
        const chartData = {
            labels: occupations.map(([key]) => key),
            datasets: [{
                data: occupations.map(([, value]) => Number(value) || 0),
                backgroundColor: ['#42A5F5', '#66BB6A', '#FFA726', '#EF5350', '#AB47BC'],
                borderWidth: 2
            }]
        };
        return (
            <div className="grid">
                <div className="col-12 md:col-6">
                    <div style={{ height: '200px' }}>
                        <Chart
                            type="pie"
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'right' } }
                            }}
                        />
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    <div className="flex flex-column gap-2">
                        <div className="flex justify-content-between p-2 surface-ground border-round">
                            <span>Total Members</span>
                            <b>{data.totalMembers || 0}</b>
                        </div>
                        <div className="flex justify-content-between p-2 surface-ground border-round">
                            <span>Male</span>
                            <b>{data.maleCount || 0}</b>
                        </div>
                        <div className="flex justify-content-between p-2 surface-ground border-round">
                            <span>Female</span>
                            <b>{data.femaleCount || 0}</b>
                        </div>
                        <div className="flex justify-content-between p-2 surface-ground border-round">
                            <span>Average Age</span>
                            <b>{(Number(data.averageAge) || 0).toFixed(1)} years</b>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [loadingStates.memberReport, errors.memberReport, memberReport]);

    // ========================================================
    // RECENT TRANSACTIONS
    // ========================================================
    const renderRecentTransactions = useCallback(() => {
        if (loadingStates.recentTransactions) {
            return <Skeleton width="100%" height="200px" />;
        }
        if (errors.recentTransactions) {
            return <Message severity="error" text={`Transactions: ${errors.recentTransactions}`} className="w-full" />;
        }
        if (!recentTransactions.length) {
            return (
                <div className="text-center p-4 text-500">
                    <i className="pi pi-inbox" style={{ fontSize: '2rem' }} />
                    <p>No recent transactions found</p>
                </div>
            );
        }
        return (
            <DataTable value={recentTransactions} responsiveLayout="scroll" size="small" stripedRows>
                <Column field="loanAccountNo" header="Loan Account" />
                <Column header="Amount" body={row => formatCurrency(row.credit || row.debit || 0)} />
                <Column field="remarks" header="Remarks" />
                <Column header="Date" body={row => formatDate(row.transactionDate)} />
                <Column field="employeeCode" header="Employee" />
            </DataTable>
        );
    }, [loadingStates.recentTransactions, errors.recentTransactions, recentTransactions, formatCurrency, formatDate]);

    // ========================================================
    // REPORT FILTERS
    // ========================================================
    const renderReportFilters = useCallback(() => (
        <div className="grid p-2 surface-ground border-round mb-3">
            <div className="col-12 md:col-3">
                <label className="text-500 text-sm">Collection From</label>
                <Calendar
                    value={reportFilters.collectionFrom}
                    onChange={e => setReportFilters(prev => ({ ...prev, collectionFrom: e.value }))}
                    dateFormat="yy-mm-dd"
                    className="w-full"
                    showIcon
                />
            </div>
            <div className="col-12 md:col-3">
                <label className="text-500 text-sm">Collection To</label>
                <Calendar
                    value={reportFilters.collectionTo}
                    onChange={e => setReportFilters(prev => ({ ...prev, collectionTo: e.value }))}
                    dateFormat="yy-mm-dd"
                    className="w-full"
                    showIcon
                />
            </div>
            <div className="col-12 md:col-3">
                <label className="text-500 text-sm">Transaction Count</label>
                <Dropdown
                    value={transactionCount}
                    options={[5, 10, 25, 50, 100].map(n => ({ label: n, value: n }))}
                    onChange={e => setTransactionCount(e.value)}
                    className="w-full"
                />
            </div>
            <div className="col-12 md:col-3 flex align-items-end">
                <Button
                    label="Refresh All"
                    icon="pi pi-refresh"
                    onClick={loadAllData}
                    className="w-full"
                    raised
                />
            </div>
        </div>
    ), [reportFilters, transactionCount, loadAllData]);

    // ========================================================
    // LOAN DETAIL
    // ========================================================
    const renderLoanDetailDialog = useCallback(() => (
        <Dialog
            header="Loan Details"
            visible={showLoanDetail}
            style={{ width: '80vw', maxWidth: '900px' }}
            onHide={() => setShowLoanDetail(false)}
        >
            {selectedLoan && (
                <div className="p-3">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <label className="text-500">Loan Account</label>
                            <div className="font-bold">{selectedLoan.loanAccountNo}</div>
                            <label className="text-500 block mt-3">Member</label>
                            <div className="font-bold">{selectedLoan.memberName}</div>
                            <label className="text-500 block mt-3">Member ID</label>
                            <div className="font-bold">{selectedLoan.memberId}</div>
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="text-500">Loan Amount</label>
                            <div className="font-bold">{formatCurrency(selectedLoan.loanAmount)}</div>
                            <label className="text-500 block mt-3">Overdue Amount</label>
                            <div className="font-bold text-danger">{formatCurrency(selectedLoan.totalOverdueAmount)}</div>
                            <label className="text-500 block mt-3">Days Overdue</label>
                            <div className="font-bold">{selectedLoan.daysOverdue || 0} days</div>
                        </div>
                    </div>
                    <Divider />
                    <div className="text-center">
                        <Button label="Close" icon="pi pi-times" onClick={() => setShowLoanDetail(false)} />
                    </div>
                </div>
            )}
        </Dialog>
    ), [showLoanDetail, selectedLoan, formatCurrency]);

    // ========================================================
    // NOT AUTHENTICATED
    // ========================================================
    if (!isAuthenticated) {
        return (
            <div className="p-4">
                <Message severity="warn" text="Please login to access the dashboard" className="w-full" style={{ borderRadius: '12px' }} />
                <div className="text-center mt-3">
                    <Button label="Go to Login" icon="pi pi-sign-in" onClick={() => window.location.href = '/login'} raised />
                </div>
            </div>
        );
    }

    // ========================================================
    // GLOBAL LOADING
    // ========================================================
    const isGlobalLoading = Object.values(loadingStates).some(value => value === true);

    // ========================================================
    // MAIN RENDER
    // ========================================================
    return (
        <div className="dashboard-container p-4">
            {/* TOKEN DEBUG - REMOVE AFTER TESTING */}
            <div className="mb-3 p-2 surface-ground border-round" style={{ fontSize: '12px' }}>
                <b>Authentication:</b> {accessToken ? 'Authenticated' : 'Not Authenticated'}
            </div>

            {/* TOOLBAR */}
            <Toolbar
                left={renderBranchToolbar}
                right={() => (
                    <div className="flex align-items-center gap-3">
                        <span className="text-sm text-500">
                            <i className="pi pi-clock mr-1" />
                            {formatDateTime(summary.lastUpdated)}
                        </span>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={loadAllData}
                            tooltip="Refresh All Data"
                            loading={isGlobalLoading}
                        />
                    </div>
                )}
                className="mb-4 dashboard-toolbar"
                style={{ borderRadius: '12px', padding: '1rem 1.5rem' }}
            />

            {/* FILTERS */}
            {renderReportFilters()}

            {/* SUMMARY */}
            {renderSummaryCards()}

            {/* LOAN STATUS */}
            <div className="grid mt-3">
                <div className="col-12">
                    <Card className="loan-status-card">{renderLoanStatusSummary()}</Card>
                </div>
            </div>

            {/* PAR */}
            <div className="grid mt-3">
                <div className="col-12">
                    <Card title="Portfolio at Risk" className="chart-card">
                        {renderPortfolioAtRisk()}
                    </Card>
                </div>
            </div>

            {/* CHARTS */}
            <div className="grid mt-4">
                <div className="col-12 lg:col-6">
                    <Card title="Monthly Collections" className="chart-card">
                        <div style={{ height: '250px' }}>{renderCollectionChart()}</div>
                    </Card>
                </div>
                <div className="col-12 lg:col-6">
                    <Card title="Monthly Disbursements" className="chart-card">
                        <div style={{ height: '250px' }}>{renderDisbursementChart()}</div>
                    </Card>
                </div>
            </div>

            {/* OVERDUE + MEMBERS */}
            <div className="grid mt-4">
                <div className="col-12 lg:col-6">
                    <Card title="Overdue Loans" className="chart-card">
                        {renderOverdueTable()}
                    </Card>
                </div>
                <div className="col-12 lg:col-6">
                    <Card title="Member Demographics" className="chart-card">
                        {renderMemberDemographics()}
                    </Card>
                </div>
            </div>

            {/* TRANSACTIONS */}
            <div className="grid mt-4">
                <div className="col-12">
                    <Card title="Recent Transactions" className="chart-card">
                        {renderRecentTransactions()}
                    </Card>
                </div>
            </div>

            {/* LOAN DIALOG */}
            {renderLoanDetailDialog()}
        </div>
    );
};

export default Dashboard;